import {
  GQLDeliveryOrder,
  GQLListScUser,
  GQLScUser,
  GQLHtUser,
  GQLDeliveryTimeCode,
  GQLOrderClassCode,
  GQLDeliveryHistory,
  GQLMedicalInstitutionMember,
  GQLMedicalInstitutionMemberPosition,
  GQLMedicalInstitutionMemberRole,
  GQLMedicalInstitutionMemberStatus,
  GQLListHtUser,
  GQLPrescriptionByPrescriptionCodePayload,
  GQLContract,
  GQLMemberContract,
  GQLListHaudyUser,
  GQLHaudyUser,
  GQLEmailContactSummary,
} from "@rx-cs-system/schema";
import { FieldPolicy, Reference } from "@apollo/client";
import dayjs from "dayjs";
import scAppIcon from "../assets/images/pdts/sc.png";
import htAppIcon from "../assets/images/pdts/ht.png";
import haudyAppIcon from "../assets/images/pdts/haudy.png";
import "../assets/images/pdts/ht-bindex.png";
import "../assets/images/pdts/ht-dh2.png";
import "../assets/images/pdts/relivion.png";
import "../assets/images/pdts/nash.png";
import "../assets/images/pdts/haudy.png";
import "../assets/images/pdts/restie.png";
import userIcon from "../assets/images/users/sample.png";
import {
  CreatePdtDocumentFields,
  DeliveryOrderFields,
  MedicalInstitutionMember,
  MedicalInstitutionMemberPosition,
  MedicalInstitutionMemberRole,
  MedicalInstitutionMemberStatus,
  UploadPdtDocumentFields,
  Contract,
  MemberContract,
  EmailContactSummary,
} from "../types";

/**
 * cache utilities
 */

// reference: https://github.com/apollographql/apollo-client/blob/b2ad938f66a99c8e6b4bd5303f930f29d50e4b62/src/utilities/policies/pagination.ts#L24
// A basic field policy that uses options.args.{offset,limit} to splice
// the incoming data into the existing array. If your arguments are called
// something different (like args.{start,count}), feel free to copy/paste
// this implementation and make the appropriate changes.
// → changed from {offset,limit} to {skip,take}
export function offsetLimitPagination<T = Reference>(
  keyArgs: string[] | false = false,
): FieldPolicy<T[]> {
  return {
    keyArgs,
    merge(existing, incoming, { args }) {
      const merged = existing ? existing.slice(0) : [];
      if (args) {
        // Assume an skip of 0 if args.skip omitted.
        const { skip = 0 } = args;
        if (skip === 0) {
          return incoming;
        } else {
          for (let i = 0; i < incoming.length; ++i) {
            merged[skip + i] = incoming[i];
          }
        }
      }

      return merged;
    },
  };
}

export function paginationForDeliveryOrder<T = Reference>(
  keyArgs: string[] | false = false,
): FieldPolicy<T[]> {
  return {
    keyArgs,
    merge(existing, incoming, { args }) {
      const merged = existing ? existing.slice(0) : [];
      if (args) {
        // Assume an skip of 0 if args.skip omitted.
        const { offset = 0 } = args;
        if (offset === 0) {
          return incoming;
        } else {
          for (let i = 0; i < incoming.length; ++i) {
            merged[offset + i] = incoming[i];
          }
        }
      }

      return merged;
    },
  };
}

/**
 * Patient
 */

// PARSERS

// Parse GQL response into the component props
export const parseScPatients = (users: GQLListScUser[]) => {
  return users.map((user) => ({
    id: user.id,
    name: `${user.lastName || ""} ${user.firstName || ""}`,
    appName: user.appName,
    // FIXME: hardcoded
    appImage: scAppIcon,
    prescriptionCode: user.prescriptionCode,
    prescribedAt:
      (user.prescribedAt && dayjs(user.prescribedAt).format("YYYY/MM/DD")) ||
      "",
  }));
};

// Parse GQL response into the component props
export const parseHtPatients = (users: GQLListHtUser[]) => {
  return users.map((user) => ({
    id: user.id,
    name: `${user.nickname || ""}`,
    appName: user.appName,
    // FIXME: hardcoded
    appImage: htAppIcon,
    prescriptionCode: user.prescriptionCode,
    prescribedAt:
      (user.prescribedAt && dayjs(user.prescribedAt).format("YYYY/MM/DD")) ||
      "",
  }));
};

export const parseHaudyPatients = (users: GQLListHaudyUser[]) => {
  return users.map((user) => ({
    id: user.id,
    nickname: user.nickname || "",
    appName: user.appName,
    appImage: haudyAppIcon,
    prescriptionCode: user.prescriptionCode,
    prescribedAt:
      (user.prescribedAt && dayjs(user.prescribedAt).format("YYYY/MM/DD")) ||
      "",
  }));
};

// Parse GQL response into the component props
export const parsePatientSc = (user: GQLScUser) => {
  return {
    patientId: user.id,
    name: `${user.lastName || ""} ${user.firstName || ""}`,
    // FIXME: hardcoded
    memberImage: userIcon,
    appName: user.appName,
    phoneNumber: user.phoneNumber,
    email: user.email,
    coCheckerSerial: user.coCheckerSerial,
    birthday:
      (user.birthday && dayjs(user.birthday).format("YYYY/MM/DD")) || "",
    registeredAt: dayjs(user.registeredAt).format("YYYY/MM/DD") || "",
    prescriptionCode: user.prescriptionCode,
    prescribedAt:
      (user.prescribedAt && dayjs(user.prescribedAt).format("YYYY/MM/DD")) ||
      "",
    medicalInstitutionId: user.medicalInstitutionId,
    medicalInstitutionName: user.medicalInstitutionName,
    prescription: user.prescription,
  };
};

export const parsePatientHt = (htUser: GQLHtUser) => {
  return {
    patientId: htUser.id,
    nickname: `${htUser.nickname || ""}`,
    // FIXME: hardcoded
    memberImage: userIcon,
    appName: htUser.appName,
    email: htUser.email,
    birthday:
      (htUser.birthday && dayjs(htUser.birthday).format("YYYY/MM/DD")) || "",
    prescriptionCode: htUser.prescriptionCode,
    prescribedAt:
      (htUser.prescribedAt &&
        dayjs(htUser.prescribedAt).format("YYYY/MM/DD")) ||
      "",
    medicalInstitutionId: htUser.medicalInstitutionId,
    medicalInstitutionName: htUser.medicalInstitutionName,
    prescription: htUser.prescription
      ? {
          name: htUser.prescription.name,
          prescriptionCureAppPdtCode: {
            statusLabelForCsSystem:
              htUser.prescription.prescriptionCureAppPdtCode
                ?.statusLabelForCsSystem,
          },
        }
      : undefined,
  };
};

export const parsePatientHaudy = (haudyUser: GQLHaudyUser) => ({
  id: haudyUser.id,
  prescriptionCode: haudyUser.prescriptionCode,
  prescribedAt:
    (haudyUser.prescribedAt &&
      dayjs(haudyUser.prescribedAt).format("YYYY/MM/DD")) ||
    "",
  status: haudyUser.status,
  nickname: haudyUser.nickname || "",
  name: haudyUser.name || "",
  memberImage: userIcon,
  birthMonth:
    (haudyUser.birthMonth && dayjs(haudyUser.birthMonth).format("YYYY/MM")) ||
    "",
  appName: haudyUser.appName,
  medicalInstitutionId: haudyUser.medicalInstitutionId,
  medicalInstitutionName: haudyUser.medicalInstitutionName,
  firstConsultationAt:
    (haudyUser.firstConsultationAt &&
      dayjs(haudyUser.firstConsultationAt).format("YYYY/MM/DD")) ||
    "",
  latestConsultationAt:
    (haudyUser.latestConsultationAt &&
      dayjs(haudyUser.latestConsultationAt).format("YYYY/MM/DD")) ||
    "",
  drinkingRecordRate: haudyUser.drinkingRecordRate
    ? `${haudyUser.drinkingRecordRate} %`
    : "",
});

export const parseDeliveryOrder = (deliveryOrder: GQLDeliveryOrder) => {
  return {
    id: deliveryOrder.id,
    name: deliveryOrder.name,
    postalCode: deliveryOrder.postalCode,
    address: deliveryOrder.address,
    phoneNumber: deliveryOrder.phoneNumber,
    deliveryDate: deliveryOrder.deliveryDate
      ? dayjs(deliveryOrder.deliveryDate).format("YYYY/MM/DD")
      : "",
    deliveryTime: deliveryOrder.deliveryTime,
    deliveryProducts: deliveryOrder.deliveryProducts,
    orderClass: deliveryOrder.orderClass?.name || "",
  };
};

// Default state
export const defaultCreateDeliveryOrderFields: DeliveryOrderFields = {
  name: "",
  postalCode: "",
  phoneNumber: "",
  address: "",
  deliveryDate: "",
  deliveryTimeCode: "YT0000",
  orderClassCode: "MEDICAL_INSTITUTION_SHIPPING",
  products: [
    {
      productCode: "",
      quantity: undefined,
    },
  ],
};

export const defaultUploadPdtDocumentFields: UploadPdtDocumentFields = {
  name: "",
  version: 1,
  enableFlag: true,
  file: {
    lastModified: 20210101,
    name: "",
    size: 1,
    type: "",
    slice: () => new Blob(),
    stream: () => new ReadableStream(),
    text: async () => "",
    arrayBuffer: async () => new ArrayBuffer(1),
    webkitRelativePath: "",
  } as unknown as File,
};

export const defaultCreatePdtDocumentFields: CreatePdtDocumentFields = {
  name: "",
  version: 1,
  enableFlag: true,
  url: "",
};

export const parseDeliveryOrders = (deliveryOrders: GQLDeliveryOrder[]) => {
  return deliveryOrders.map((deliveryOrder) => ({
    id: deliveryOrder.id,
    name: deliveryOrder.name,
    // FIXME: hardcoded
    image: userIcon,
    postalCode: deliveryOrder.postalCode,
    address: deliveryOrder.address,
    phoneNumber: deliveryOrder.phoneNumber,
    deliveryDate: deliveryOrder.deliveryDate
      ? dayjs(deliveryOrder.deliveryDate).format("YYYY/MM/DD")
      : "",
    deliveryTime: deliveryOrder.deliveryTime,
    deliveryHistory: deliveryOrder.deliveryHistory.filter(
      (deliveryHistory: GQLDeliveryHistory) => {
        return (
          deliveryHistory.serialNumber !== undefined &&
          deliveryHistory.serialNumber !== null &&
          deliveryHistory.serialNumber !== ""
        );
      },
    ),
    deliveryProducts: deliveryOrder.deliveryProducts,
  }));
};

export const parseDeliveryOrderDetail = (deliveryOrder: GQLDeliveryOrder) => {
  return {
    id: deliveryOrder.id,
    name: deliveryOrder.name,
    // FIXME: hardcoded
    image: userIcon,
    postalCode: deliveryOrder.postalCode,
    address: deliveryOrder.address,
    phoneNumber: deliveryOrder.phoneNumber,
    deliveryDate: deliveryOrder.deliveryDate
      ? dayjs(deliveryOrder.deliveryDate).format("YYYY/MM/DD")
      : "",
    deliveryTime: deliveryOrder.deliveryTime,
    deliveryHistory: deliveryOrder.deliveryHistory,
    deliveryProducts: deliveryOrder.deliveryProducts,
  };
};

export const GQLDeliveryTimeCodes: Record<string, GQLDeliveryTimeCode> = {
  YT0000: GQLDeliveryTimeCode.Yt0000,
  YT0812: GQLDeliveryTimeCode.Yt0812,
  YT1416: GQLDeliveryTimeCode.Yt1416,
  YT1618: GQLDeliveryTimeCode.Yt1618,
  YT1820: GQLDeliveryTimeCode.Yt1820,
  YT2021: GQLDeliveryTimeCode.Yt2021,
};

export const GQLDeliveryOrderClasses: Record<string, GQLOrderClassCode> = {
  MEDICAL_INSTITUTION_REPLACEMENT_SHIPPING:
    GQLOrderClassCode.MedicalInstitutionReplacementShipping,
  PATIENT_REPLACEMENT_SHIPPING: GQLOrderClassCode.PatientReplacementShipping,
};

/**
 * Medical Institution Member
 */

const positionLabels: Record<
  GQLMedicalInstitutionMemberPosition,
  MedicalInstitutionMemberPosition
> = {
  [GQLMedicalInstitutionMemberPosition.Doctor]: "医師",
  [GQLMedicalInstitutionMemberPosition.Nurse]: "看護師",
  [GQLMedicalInstitutionMemberPosition.Staff]: "スタッフ",
};

const roleLabels: Record<
  GQLMedicalInstitutionMemberRole,
  MedicalInstitutionMemberRole
> = {
  [GQLMedicalInstitutionMemberRole.Admin]: "管理者",
  [GQLMedicalInstitutionMemberRole.Staff]: "スタッフ",
};

const statusLables: Record<
  GQLMedicalInstitutionMemberStatus,
  MedicalInstitutionMemberStatus
> = {
  [GQLMedicalInstitutionMemberStatus.Enabled]: "有効",
  [GQLMedicalInstitutionMemberStatus.Disabled]: "停止中",
  [GQLMedicalInstitutionMemberStatus.Invited]: "招待中",
  [GQLMedicalInstitutionMemberStatus.SecretcodeReissued]: "再発行済み",
};

const parsePositionsToLabel = (
  positions: GQLMedicalInstitutionMemberPosition[],
) => positions.map((position) => positionLabels[position]);

const parseRolesToLabel = (roles: GQLMedicalInstitutionMemberRole[]) =>
  roles.map((role) => roleLabels[role]);

export const parseMemberContracts = (
  gqlMemberContracts: GQLMemberContract[],
): MemberContract[] =>
  gqlMemberContracts.map((gqlContract) => ({
    id: gqlContract.id,
    pdt: {
      id: gqlContract.pdt.id,
      name: gqlContract.pdt.name,
      iconPath: `/${gqlContract.pdt.iconPath}`,
    },
  }));

export const parseMembers = (
  GQLMedicalInstitutionMembers: GQLMedicalInstitutionMember[],
): MedicalInstitutionMember[] =>
  GQLMedicalInstitutionMembers.map((GQLMember) => ({
    id: GQLMember.id,
    name: GQLMember.name,
    position: parsePositionsToLabel(GQLMember.position),
    role: parseRolesToLabel(GQLMember.role),
    status: statusLables[GQLMember.status],
    contracts: parseMemberContracts(GQLMember.contracts || []),
  }));

export const parseContracts = (gqlContracts: GQLContract[]): Contract[] =>
  gqlContracts.map((gqlContract) => {
    const manager = gqlContract.manager
      ? {
          id: gqlContract.manager.id,
          name: gqlContract.manager.name,
          department: gqlContract.manager.department,
          emailAddress: gqlContract.manager.emailAddress,
          welcomeMailSentAt: gqlContract.manager.welcomeMailSentAt,
        }
      : undefined;
    const responsiblePerson = gqlContract.responsiblePerson
      ? {
          id: gqlContract.responsiblePerson.id,
          emailContact: {
            id: gqlContract.responsiblePerson!.emailContact.id!,
            name: gqlContract.responsiblePerson!.emailContact.name!,
            department: gqlContract.responsiblePerson!.emailContact.department!,
            emailAddress:
              gqlContract.responsiblePerson!.emailContact.emailAddress!,
          },
          welcomeMailSentAt: gqlContract.responsiblePerson!.welcomeMailSentAt,
        }
      : undefined;
    const notificationRecipients = gqlContract.notificationRecipients?.map(
      (recipient) => ({
        id: recipient.id,
        emailContact: {
          id: recipient.emailContact.id!,
          name: recipient.emailContact.name!,
          department: recipient.emailContact.department!,
          emailAddress: recipient.emailContact.emailAddress!,
        },
      }),
    );

    return {
      id: gqlContract.id || "",
      initial: gqlContract.initial,
      pdt: {
        id: gqlContract.pdt.id || "",
        name: gqlContract.pdt.name || "",
        iconPath: `/${gqlContract.pdt.iconPath}`,
      },
      manager,
      responsiblePerson,
      notificationRecipients,
    };
  });

export function parseEmailContactSummary(
  summary: GQLEmailContactSummary,
): EmailContactSummary {
  return {
    id: summary.id,
    name: summary.name,
    department: summary.department || undefined,
    emailAddress: summary.emailAddress,
    responsiblePersons: (summary.responsiblePersons || []).map((person) => ({
      id: person!.id,
      code: person!.code,
      name: person!.name,
      iconPath: `/${person!.iconPath}`,
    })),
    notificationRecipients: (summary.notificationRecipients || []).map(
      (recipient) => ({
        id: recipient!.id,
        code: recipient!.code,
        name: recipient!.name,
        iconPath: `/${recipient!.iconPath}`,
      }),
    ),
  };
}

/**
 * Prescription
 */

export const parsePrescriptionCancel = (
  prescription: GQLPrescriptionByPrescriptionCodePayload,
) => {
  return {
    id: prescription.id,
    name: prescription.name,
    birthday:
      (prescription.birthday &&
        dayjs(prescription.birthday).format("YYYY/MM/DD")) ||
      "",
    createdAt:
      (prescription.createdAt &&
        dayjs(prescription.createdAt).format("YYYY/MM/DD hh:mm")) ||
      "",
    appName: prescription.appName,
    medicalInstitutionName: prescription.medicalInstitutionName,
    codeId: prescription.codeId,
    code: prescription.code,
    codeStatus: prescription.codeStatus,
  };
};
